// sample code ต่อภาษาในหน้า /document-api สร้างจาก request ของ Postman collection (assets/files/api*.json)
//
// กติกาที่ server ตรวจ (PaymentBroker-ApiPartner middleware/authorization.js):
//   x-signature = HMAC-SHA256(client_secret, client_id + x-timestamp + JSON.stringify(body)) เป็น hex
// server แปลง body ที่ได้กลับเป็น string ด้วย JSON.stringify ทุกภาษาจึงเซ็นและส่ง "string ชุดเดียวกัน"
// (bodyCompact ด้านล่าง สร้างด้วย JSON.stringify เหมือน server) ไม่ให้ภาษานั้น serialize ใหม่เอง
// GET ไม่มี body ให้เซ็น "{}"

const SIGNED_HEADERS = ["x-timestamp", "x-signature"];

export function generateSnippets(req, type) {
  if (!req) return "";

  const url = req?.url?.raw || `${req?.url?.host?.join("")}/${req?.url?.path?.join("/")}`;
  const method = (req?.method || "GET").toUpperCase();
  const token = req?.auth?.bearer?.[0]?.value || null;

  const certPath = "./cert/certificate.pem";
  const keyPath = "./cert/private.key";

  // body ใน collection มีคอมเมนต์ // ต่อท้ายบรรทัด ตัดออกก่อน parse
  let parsedBody = {};
  try {
    parsedBody = JSON.parse((req?.body?.raw || "").replace(/\/\/.*$/gm, "").trim() || "{}");
  } catch (e) {
    parsedBody = {};
  }

  const includeBody = method !== "GET";
  const bodyCompact = includeBody ? JSON.stringify(parsedBody) : "{}";
  const bodyLiteral = JSON.stringify(bodyCompact); // string literal ที่ใช้ได้ใน JS / Python / Go / Java

  // header ทั้งหมด (curl) และ header ที่ไม่ต้องคำนวณ (ภาษาอื่นคำนวณ x-timestamp / x-signature เอง)
  const headers = (req?.header || []).map((h) => [h.key, h.value]);
  if (token && !headers.some(([k]) => k.toLowerCase() === "authorization")) headers.push(["Authorization", `Bearer ${token}`]);
  const staticHeaders = headers.filter(([k]) => !SIGNED_HEADERS.includes(k.toLowerCase()));
  const staticHeadersObject = Object.fromEntries(staticHeaders);

  const snippets = {
    curl: `# replace {{x-timestamp}} / {{x-signature}} with values computed as shown in X-Signature
curl -X ${method} \\
  "${url}" \\
  --cert ${certPath} \\
  --key ${keyPath} \\
  ${headers.map(([k, v]) => `-H "${k}: ${v}"`).join(" \\\n  ")}${includeBody ? ` \\\n  -d '${bodyCompact}'` : ""}`,

    node: `const axios = require('axios');
const fs = require('fs');
const crypto = require('crypto');
const https = require('https');

const client_id = "{{client_id}}";
const client_secret = "{{client_secret}}";

// sign exactly the string you send
const requestBody = ${bodyLiteral};
const timestamp = Math.floor(Date.now() / 1000).toString(); // second (Unix Time)
const signature = crypto.createHmac('sha256', client_secret).update(client_id + timestamp + requestBody).digest('hex');

// Our API is served by a publicly trusted certificate, so leave "ca" unset and
// let Node use its built-in root store.
const agent = new https.Agent({
  cert: fs.readFileSync('${certPath}'),
  key: fs.readFileSync('${keyPath}'),
  rejectUnauthorized: true
});

axios({
  method: '${method.toLowerCase()}',
  url: '${url}',
  headers: {
${staticHeaders.map(([k, v]) => `    "${k}": "${v}",`).join("\n")}
    "x-timestamp": timestamp,
    "x-signature": signature
  },
  httpsAgent: agent,${includeBody ? "\n  data: requestBody," : ""}
}).then(res => {
  console.log(res.data);
}).catch(err => {
  console.error(err.response?.data || err.message);
});`,

    python: `import requests
import time
import hmac
import hashlib

client_id = "{{client_id}}"
client_secret = "{{client_secret}}"

# sign exactly the string you send (do not re-serialize with json.dumps)
request_body = ${bodyLiteral}
timestamp = str(int(time.time()))
signature = hmac.new(
    client_secret.encode('utf-8'),
    (client_id + timestamp + request_body).encode('utf-8'),
    hashlib.sha256
).hexdigest()

url = "${url}"
headers = ${JSON.stringify(staticHeadersObject, null, 4)}
headers["x-timestamp"] = timestamp
headers["x-signature"] = signature

response = requests.request("${method}", url, headers=headers${includeBody ? ", data=request_body.encode('utf-8')" : ""}, cert=("${certPath}", "${keyPath}"), verify=True)
print(response.json())`,

    go: `package main

import (${includeBody ? '\n  "bytes"' : ""}
  "crypto/hmac"
  "crypto/sha256"
  "crypto/tls"
  "encoding/hex"
  "fmt"
  "io"
  "log"
  "net/http"
  "strconv"
  "time"
)

func main() {
  cert, err := tls.LoadX509KeyPair("${certPath}", "${keyPath}")
  if err != nil {
    log.Fatal(err)
  }
  // No RootCAs override: the system trust store already trusts our API's certificate.
  client := &http.Client{Transport: &http.Transport{TLSClientConfig: &tls.Config{Certificates: []tls.Certificate{cert}}}}

  clientID := "{{client_id}}"
  clientSecret := "{{client_secret}}"

  // sign exactly the string you send
  requestBody := ${bodyLiteral}
  timestamp := strconv.FormatInt(time.Now().Unix(), 10)
  h := hmac.New(sha256.New, []byte(clientSecret))
  h.Write([]byte(clientID + timestamp + requestBody))
  signature := hex.EncodeToString(h.Sum(nil))

  req, _ := http.NewRequest("${method}", "${url}", ${includeBody ? "bytes.NewBufferString(requestBody)" : "nil"})
${staticHeaders.map(([k, v]) => `  req.Header.Set("${k}", "${v}")`).join("\n")}
  req.Header.Set("x-timestamp", timestamp)
  req.Header.Set("x-signature", signature)

  resp, err := client.Do(req)
  if err != nil {
    log.Fatal(err)
  }
  defer resp.Body.Close()

  body, _ := io.ReadAll(resp.Body)
  fmt.Println(string(body))
}`,

    csharp: `using System;
using System.Net.Http;
using System.Security.Cryptography;
using System.Security.Cryptography.X509Certificates;
using System.Text;
using System.Threading.Tasks;

class Program {
  static async Task Main() {
    // certificate + private key from the PEM files (.NET 5+). Re-export so Windows can use the key for TLS.
    var pem = X509Certificate2.CreateFromPemFile("${certPath}", "${keyPath}");
    var handler = new HttpClientHandler();
    handler.ClientCertificates.Add(new X509Certificate2(pem.Export(X509ContentType.Pkcs12)));
    var client = new HttpClient(handler);

    string clientId = "{{client_id}}";
    string clientSecret = "{{client_secret}}";

    // sign exactly the string you send
    string requestBody = @"${bodyCompact.replace(/"/g, '""')}";
    string timestamp = DateTimeOffset.UtcNow.ToUnixTimeSeconds().ToString();
    string signature = GenerateHMACSHA256(clientId + timestamp + requestBody, clientSecret);

    var request = new HttpRequestMessage(new HttpMethod("${method}"), "${url}");
${staticHeaders
  .filter(([k]) => k.toLowerCase() !== "content-type")
  .map(([k, v]) => `    request.Headers.TryAddWithoutValidation("${k}", "${v}");`)
  .join("\n")}
    request.Headers.TryAddWithoutValidation("x-timestamp", timestamp);
    request.Headers.TryAddWithoutValidation("x-signature", signature);${
      includeBody ? `\n    request.Content = new StringContent(requestBody, Encoding.UTF8, "application/json");` : ""
    }

    var response = await client.SendAsync(request);
    Console.WriteLine(await response.Content.ReadAsStringAsync());
  }

  static string GenerateHMACSHA256(string data, string secret) {
    using (var hmac = new HMACSHA256(Encoding.UTF8.GetBytes(secret))) {
      var hash = hmac.ComputeHash(Encoding.UTF8.GetBytes(data));
      return BitConverter.ToString(hash).Replace("-", "").ToLower();
    }
  }
}`,

    java: `import javax.net.ssl.*;
import java.io.*;
import java.net.*;
import java.security.KeyStore;
import javax.crypto.Mac;
import javax.crypto.spec.SecretKeySpec;
import java.nio.charset.StandardCharsets;
import java.time.Instant;

public class Main {
  public static void main(String[] args) throws Exception {
    // certificate.p12 is created from certificate.pem + private.key (see Tips above)
    char[] password = "your_password".toCharArray();
    KeyStore ks = KeyStore.getInstance("PKCS12");
    ks.load(new FileInputStream("./cert/certificate.p12"), password);
    KeyManagerFactory kmf = KeyManagerFactory.getInstance("SunX509");
    kmf.init(ks, password);

    // No truststore: our API is served by a publicly trusted certificate (JDK cacerts).
    SSLContext sc = SSLContext.getInstance("TLS");
    sc.init(kmf.getKeyManagers(), null, null);
    HttpsURLConnection.setDefaultSSLSocketFactory(sc.getSocketFactory());

    String clientId = "{{client_id}}";
    String clientSecret = "{{client_secret}}";

    // sign exactly the string you send
    String requestBody = ${bodyLiteral};
    String timestamp = String.valueOf(Instant.now().getEpochSecond());
    String signature = hmacSha256(clientId + timestamp + requestBody, clientSecret);

    HttpsURLConnection conn = (HttpsURLConnection) new URI("${url}").toURL().openConnection();
    conn.setRequestMethod("${method}");
${staticHeaders.map(([k, v]) => `    conn.setRequestProperty("${k}", "${v}");`).join("\n")}
    conn.setRequestProperty("x-timestamp", timestamp);
    conn.setRequestProperty("x-signature", signature);
${
  includeBody
    ? `    conn.setDoOutput(true);
    try (OutputStream os = conn.getOutputStream()) {
      os.write(requestBody.getBytes(StandardCharsets.UTF_8));
    }`
    : ""
}

    InputStream stream = conn.getResponseCode() < 400 ? conn.getInputStream() : conn.getErrorStream();
    try (BufferedReader br = new BufferedReader(new InputStreamReader(stream, StandardCharsets.UTF_8))) {
      StringBuilder response = new StringBuilder();
      String line;
      while ((line = br.readLine()) != null) response.append(line.trim());
      System.out.println("Response: " + response);
    }
  }

  public static String hmacSha256(String data, String secret) throws Exception {
    Mac mac = Mac.getInstance("HmacSHA256");
    mac.init(new SecretKeySpec(secret.getBytes(StandardCharsets.UTF_8), "HmacSHA256"));
    StringBuilder hex = new StringBuilder();
    for (byte b : mac.doFinal(data.getBytes(StandardCharsets.UTF_8))) hex.append(String.format("%02x", b));
    return hex.toString();
  }
}`,

    php: `<?php
$client_id = "{{client_id}}";
$client_secret = "{{client_secret}}";

// sign exactly the string you send (do not re-encode with json_encode)
$requestBody = '${bodyCompact.replace(/\\/g, "\\\\").replace(/'/g, "\\'")}';
$timestamp = (string) time();
$signature = hash_hmac('sha256', $client_id . $timestamp . $requestBody, $client_secret);

$headers = [
${staticHeaders.map(([k, v]) => `    "${k}: ${v}",`).join("\n")}
    "x-timestamp: " . $timestamp,
    "x-signature: " . $signature,
];

$ch = curl_init("${url}");
curl_setopt($ch, CURLOPT_CUSTOMREQUEST, "${method}");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);${includeBody ? "\ncurl_setopt($ch, CURLOPT_POSTFIELDS, $requestBody);" : ""}

// mTLS certificate. No CURLOPT_CAINFO: the system CA bundle verifies our API.
curl_setopt($ch, CURLOPT_SSLCERT, __DIR__ . "/cert/certificate.pem");
curl_setopt($ch, CURLOPT_SSLKEY, __DIR__ . "/cert/private.key");
curl_setopt($ch, CURLOPT_SSL_VERIFYPEER, true);

$response = curl_exec($ch);
echo curl_errno($ch) ? "cURL Error: " . curl_error($ch) : $response;
`,
  };

  return snippets[type] || "// Unsupported type";
}
